import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calculator, Users, Utensils, Wine, Plus, Minus, Check, ChevronDown, ChevronUp, Send, FileText } from 'lucide-react';
import { menusAPI, selectableItemsAPI, rentalsAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';

const QuoteCalculator = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [menus, setMenus] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [barItems, setBarItems] = useState([]);
  const [lateNightItems, setLateNightItems] = useState([]);
  const [addonItems, setAddonItems] = useState([]);
  const [platedItems, setPlatedItems] = useState([]);
  
  // Form state
  const [guestCount, setGuestCount] = useState(50);
  const [eventType, setEventType] = useState('offsite');
  const [selectedMenus, setSelectedMenus] = useState([]);
  const [selectedBar, setSelectedBar] = useState(null);
  const [selectedLateNight, setSelectedLateNight] = useState([]);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [selectedRentals, setSelectedRentals] = useState([]);
  const [selectedPlatedMains, setSelectedPlatedMains] = useState([]);
  const [includeGratuity, setIncludeGratuity] = useState(false);
  const [gratuityRate, setGratuityRate] = useState(0.18);
  
  // UI state
  const [expandedSections, setExpandedSections] = useState({
    menus: true,
    bar: false,
    lateNight: false,
    addons: false,
    rentals: false,
    plated: false
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [menusData, rentalsData, barData, lnData, addonsData, platedData] = await Promise.all([
        menusAPI.getAll(),
        rentalsAPI.getAll(),
        selectableItemsAPI.getBar(),
        selectableItemsAPI.getLateNight(),
        selectableItemsAPI.getAddons(),
        selectableItemsAPI.getPlated()
      ]);
      setMenus(menusData);
      setRentals(rentalsData);
      setBarItems(barData);
      setLateNightItems(lnData);
      setAddonItems(addonsData);
      setPlatedItems(platedData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const toggleMenu = (menu) => {
    setSelectedMenus(prev => {
      const exists = prev.find(m => m.id === menu.id);
      if (exists) return prev.filter(m => m.id !== menu.id);
      return [...prev, menu];
    });
  };

  const toggleAddon = (item) => {
    setSelectedAddons(prev => {
      const exists = prev.find(i => i.id === item.id);
      if (exists) return prev.filter(i => i.id !== item.id);
      return [...prev, item];
    });
  };

  const toggleLateNight = (item) => {
    setSelectedLateNight(prev => {
      const exists = prev.find(i => i.id === item.id);
      if (exists) return prev.filter(i => i.id !== item.id);
      return [...prev, item];
    });
  };

  const toggleRental = (rental) => {
    setSelectedRentals(prev => {
      const exists = prev.find(r => r.id === rental.id);
      if (exists) return prev.filter(r => r.id !== rental.id);
      return [...prev, { ...rental, quantity: 1 }];
    });
  };

  const updateRentalQty = (id, qty) => {
    setSelectedRentals(prev => 
      prev.map(r => r.id === id ? { ...r, quantity: Math.max(1, qty) } : r)
    );
  };

  const togglePlatedMain = (item) => {
    setSelectedPlatedMains(prev => {
      const exists = prev.find(i => i.id === item.id);
      if (exists) return prev.filter(i => i.id !== item.id);
      if (prev.length < 3) return [...prev, { ...item, quantity: Math.floor(guestCount / 3) }];
      return prev;
    });
  };

  const updatePlatedQty = (id, qty) => {
    setSelectedPlatedMains(prev => 
      prev.map(m => m.id === id ? { ...m, quantity: Math.max(0, qty) } : m)
    );
  };

  // Calculate totals
  const calculateTotals = () => {
    // Menu subtotal (exclude plated - they have custom pricing)
    const menuSubtotal = selectedMenus
      .filter(m => m.category !== 'Plated')
      .reduce((sum, m) => sum + (m.price_per_person || 0), 0) * guestCount;
    
    // Plated dinner subtotal
    const platedSubtotal = selectedPlatedMains.reduce((sum, m) => 
      sum + (m.quantity * (m.price_per_person || 0)), 0);
    
    // Bar subtotal
    const barSubtotal = selectedBar ? (selectedBar.price_per_person || 0) * guestCount : 0;
    
    // Add-ons subtotal
    const addonsSubtotal = selectedAddons.reduce((sum, a) => 
      sum + (a.price_per_person || 0), 0) * guestCount;
    
    // Rentals subtotal
    const rentalsSubtotal = selectedRentals.reduce((sum, r) => 
      sum + (r.price * r.quantity), 0);
    
    const subtotal = menuSubtotal + platedSubtotal + barSubtotal + addonsSubtotal + rentalsSubtotal;
    const taxRate = 0.13;
    const taxAmount = subtotal * taxRate;
    const gratuityAmount = includeGratuity ? subtotal * gratuityRate : 0;
    const total = subtotal + taxAmount + gratuityAmount;

    return {
      menuSubtotal,
      platedSubtotal,
      barSubtotal,
      addonsSubtotal,
      rentalsSubtotal,
      subtotal,
      taxAmount,
      gratuityAmount,
      total
    };
  };

  const totals = calculateTotals();

  const getMenusByCategory = () => {
    const categories = {};
    menus.forEach(menu => {
      const cat = menu.category || 'Other';
      if (!categories[cat]) categories[cat] = [];
      categories[cat].push(menu);
    });
    return categories;
  };

  const handleRequestQuote = () => {
    // Navigate to contact or create a quote request
    const quoteData = {
      guestCount,
      eventType,
      selectedMenus,
      selectedBar,
      selectedLateNight,
      selectedAddons,
      selectedRentals,
      selectedPlatedMains,
      totals
    };
    // Store in sessionStorage for quote request form
    sessionStorage.setItem('quoteData', JSON.stringify(quoteData));
    navigate('/quote-request');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#f5f5dc] to-[#e8dcc8] flex items-center justify-center">
        <div className="animate-spin text-[#1e3a5f] text-2xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f5f5dc] to-[#e8dcc8]">
      {/* Header */}
      <div className="bg-[#1e3a5f] text-white py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <Calculator size={32} className="text-[#c9a227]" />
            <h1 className="text-3xl font-bold">Catering Quote Calculator</h1>
          </div>
          <p className="text-gray-200">Get an instant estimate for your catering needs</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-4 md:p-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Selections */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info */}
            <Card className="border-none shadow-lg">
              <CardHeader className="bg-[#8b7355] text-white rounded-t-lg">
                <CardTitle className="flex items-center gap-2">
                  <Users size={20} />
                  Event Details
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Number of Guests</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setGuestCount(Math.max(10, guestCount - 10))}
                      >
                        <Minus size={16} />
                      </Button>
                      <Input
                        type="number"
                        value={guestCount}
                        onChange={(e) => setGuestCount(parseInt(e.target.value) || 10)}
                        className="w-24 text-center text-lg font-bold"
                        data-testid="guest-count-input"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setGuestCount(guestCount + 10)}
                      >
                        <Plus size={16} />
                      </Button>
                    </div>
                  </div>
                  <div>
                    <Label>Event Type</Label>
                    <Select value={eventType} onValueChange={setEventType}>
                      <SelectTrigger className="mt-1" data-testid="event-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="onsite">Onsite Event</SelectItem>
                        <SelectItem value="offsite">Offsite Event</SelectItem>
                        <SelectItem value="tournament">Tournament</SelectItem>
                        <SelectItem value="cocktail_coach">Cocktail Coach</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Menu Selection */}
            <Card className="border-none shadow-lg">
              <CardHeader 
                className="bg-[#1e3a5f] text-white rounded-t-lg cursor-pointer"
                onClick={() => toggleSection('menus')}
              >
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Utensils size={20} />
                    Menu Packages
                    {selectedMenus.length > 0 && (
                      <span className="bg-[#c9a227] text-[#1e3a5f] px-2 py-0.5 rounded-full text-sm">
                        {selectedMenus.length} selected
                      </span>
                    )}
                  </span>
                  {expandedSections.menus ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </CardTitle>
              </CardHeader>
              {expandedSections.menus && (
                <CardContent className="p-4 space-y-4 max-h-96 overflow-y-auto">
                  {Object.entries(getMenusByCategory()).map(([category, categoryMenus]) => (
                    <div key={category}>
                      <h4 className="font-semibold text-[#8b7355] mb-2">{category}</h4>
                      <div className="space-y-2">
                        {categoryMenus.map(menu => {
                          const isSelected = selectedMenus.find(m => m.id === menu.id);
                          return (
                            <div
                              key={menu.id}
                              onClick={() => toggleMenu(menu)}
                              className={`p-3 rounded-lg border cursor-pointer transition-all ${
                                isSelected
                                  ? 'border-[#1e3a5f] bg-[#1e3a5f] text-white'
                                  : 'border-gray-200 bg-white hover:border-[#1e3a5f]'
                              }`}
                              data-testid={`menu-${menu.id}`}
                            >
                              <div className="flex justify-between items-center">
                                <div className="flex items-center gap-2">
                                  {isSelected && <Check size={16} />}
                                  <span className="font-medium">{menu.name}</span>
                                </div>
                                {menu.price_per_person && (
                                  <span className="font-bold">${menu.price_per_person}/pp</span>
                                )}
                              </div>
                              {menu.description && (
                                <p className={`text-sm mt-1 ${isSelected ? 'text-gray-200' : 'text-gray-500'}`}>
                                  {menu.description}
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </CardContent>
              )}
            </Card>

            {/* Bar Packages */}
            <Card className="border-none shadow-lg">
              <CardHeader 
                className="bg-[#8b7355] text-white rounded-t-lg cursor-pointer"
                onClick={() => toggleSection('bar')}
              >
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Wine size={20} />
                    Bar Packages
                    {selectedBar && (
                      <span className="bg-[#c9a227] text-[#1e3a5f] px-2 py-0.5 rounded-full text-sm">
                        ${selectedBar.price_per_person}/pp
                      </span>
                    )}
                  </span>
                  {expandedSections.bar ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </CardTitle>
              </CardHeader>
              {expandedSections.bar && (
                <CardContent className="p-4 space-y-2">
                  {barItems.map(item => {
                    const isSelected = selectedBar?.id === item.id;
                    return (
                      <div
                        key={item.id}
                        onClick={() => setSelectedBar(isSelected ? null : item)}
                        className={`p-3 rounded-lg border cursor-pointer transition-all ${
                          isSelected
                            ? 'border-[#c9a227] bg-[#c9a227] text-white'
                            : 'border-gray-200 bg-white hover:border-[#c9a227]'
                        }`}
                        data-testid={`bar-${item.id}`}
                      >
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-2">
                            {isSelected && <Check size={16} />}
                            <span className="font-medium">{item.name}</span>
                          </div>
                          <span className="font-bold">${item.price_per_person}/pp</span>
                        </div>
                        {item.description && (
                          <p className={`text-sm mt-1 ${isSelected ? 'text-gray-200' : 'text-gray-500'}`}>
                            {item.description}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </CardContent>
              )}
            </Card>

            {/* Late Night Options */}
            <Card className="border-none shadow-lg">
              <CardHeader 
                className="bg-[#1e3a5f] text-white rounded-t-lg cursor-pointer"
                onClick={() => toggleSection('lateNight')}
              >
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    🌙 Late Night Options
                    {selectedLateNight.length > 0 && (
                      <span className="bg-[#c9a227] text-[#1e3a5f] px-2 py-0.5 rounded-full text-sm">
                        {selectedLateNight.length} selected
                      </span>
                    )}
                  </span>
                  {expandedSections.lateNight ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </CardTitle>
              </CardHeader>
              {expandedSections.lateNight && (
                <CardContent className="p-4 grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                  {lateNightItems.map(item => {
                    const isSelected = selectedLateNight.find(i => i.id === item.id);
                    return (
                      <div
                        key={item.id}
                        onClick={() => toggleLateNight(item)}
                        className={`p-3 rounded-lg border cursor-pointer transition-all text-sm ${
                          isSelected
                            ? 'border-[#1e3a5f] bg-[#1e3a5f] text-white'
                            : 'border-gray-200 bg-white hover:border-[#1e3a5f]'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          {isSelected && <Check size={14} />}
                          <span className="font-medium">{item.name}</span>
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              )}
            </Card>

            {/* Add-Ons */}
            <Card className="border-none shadow-lg">
              <CardHeader 
                className="bg-[#8b7355] text-white rounded-t-lg cursor-pointer"
                onClick={() => toggleSection('addons')}
              >
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    ➕ Add-Ons
                    {selectedAddons.length > 0 && (
                      <span className="bg-[#c9a227] text-[#1e3a5f] px-2 py-0.5 rounded-full text-sm">
                        {selectedAddons.length} items (${selectedAddons.reduce((s,a) => s + (a.price_per_person||0), 0).toFixed(2)}/pp)
                      </span>
                    )}
                  </span>
                  {expandedSections.addons ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </CardTitle>
              </CardHeader>
              {expandedSections.addons && (
                <CardContent className="p-4 space-y-4 max-h-96 overflow-y-auto">
                  {['appetizer', 'salad', 'side', 'main', 'dessert', 'beverage'].map(cat => {
                    const items = addonItems.filter(i => i.sub_category === cat);
                    if (items.length === 0) return null;
                    return (
                      <div key={cat}>
                        <h4 className="font-semibold text-[#8b7355] mb-2 capitalize">{cat}s</h4>
                        <div className="grid grid-cols-2 gap-2">
                          {items.map(item => {
                            const isSelected = selectedAddons.find(i => i.id === item.id);
                            return (
                              <div
                                key={item.id}
                                onClick={() => toggleAddon(item)}
                                className={`p-2 rounded-lg border cursor-pointer transition-all text-sm ${
                                  isSelected
                                    ? 'border-[#c9a227] bg-[#c9a227] text-white'
                                    : 'border-gray-200 bg-white hover:border-[#c9a227]'
                                }`}
                              >
                                <div className="flex justify-between items-center">
                                  <div className="flex items-center gap-1">
                                    {isSelected && <Check size={12} />}
                                    <span>{item.name}</span>
                                  </div>
                                  <span className="font-bold">${item.price_per_person}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              )}
            </Card>

            {/* Plated Dinner */}
            <Card className="border-none shadow-lg">
              <CardHeader 
                className="bg-[#1e3a5f] text-white rounded-t-lg cursor-pointer"
                onClick={() => toggleSection('plated')}
              >
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    🍽️ Plated Dinner Options
                    {selectedPlatedMains.length > 0 && (
                      <span className="bg-[#c9a227] text-[#1e3a5f] px-2 py-0.5 rounded-full text-sm">
                        {selectedPlatedMains.length}/3 mains
                      </span>
                    )}
                  </span>
                  {expandedSections.plated ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </CardTitle>
              </CardHeader>
              {expandedSections.plated && (
                <CardContent className="p-4 space-y-4">
                  <p className="text-sm text-gray-600">Select up to 3 main courses. Guests will choose one option.</p>
                  <div className="space-y-2">
                    {platedItems.filter(i => i.sub_category === 'main').map(item => {
                      const selected = selectedPlatedMains.find(m => m.id === item.id);
                      return (
                        <div
                          key={item.id}
                          className={`p-3 rounded-lg border transition-all ${
                            selected
                              ? 'border-[#1e3a5f] bg-[#1e3a5f]/10'
                              : 'border-gray-200 bg-white'
                          }`}
                        >
                          <div className="flex justify-between items-center">
                            <div
                              className="flex-1 cursor-pointer"
                              onClick={() => togglePlatedMain(item)}
                            >
                              <div className="flex items-center gap-2">
                                {selected && <Check size={16} className="text-[#1e3a5f]" />}
                                <span className="font-medium">{item.name}</span>
                              </div>
                              {item.description && (
                                <p className="text-sm text-gray-500 ml-6">{item.description}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="font-bold text-[#1e3a5f]">${item.price_per_person}/pp</span>
                              {selected && (
                                <div className="flex items-center gap-1 border rounded">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); updatePlatedQty(item.id, selected.quantity - 1); }}
                                    className="px-2 py-1 hover:bg-gray-100"
                                  >-</button>
                                  <span className="px-2 text-sm font-medium">{selected.quantity}</span>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); updatePlatedQty(item.id, selected.quantity + 1); }}
                                    className="px-2 py-1 hover:bg-gray-100"
                                  >+</button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {selectedPlatedMains.length > 0 && (
                    <div className={`p-3 rounded-lg ${
                      selectedPlatedMains.reduce((s,m) => s + m.quantity, 0) === guestCount
                        ? 'bg-green-50 border border-green-200'
                        : 'bg-amber-50 border border-amber-200'
                    }`}>
                      <div className="flex justify-between">
                        <span>Total Orders:</span>
                        <span className="font-bold">
                          {selectedPlatedMains.reduce((s,m) => s + m.quantity, 0)} / {guestCount} guests
                        </span>
                      </div>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>

            {/* Rentals */}
            <Card className="border-none shadow-lg">
              <CardHeader 
                className="bg-[#8b7355] text-white rounded-t-lg cursor-pointer"
                onClick={() => toggleSection('rentals')}
              >
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    📦 Rentals & Décor
                    {selectedRentals.length > 0 && (
                      <span className="bg-[#c9a227] text-[#1e3a5f] px-2 py-0.5 rounded-full text-sm">
                        {selectedRentals.length} items
                      </span>
                    )}
                  </span>
                  {expandedSections.rentals ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </CardTitle>
              </CardHeader>
              {expandedSections.rentals && (
                <CardContent className="p-4 space-y-2 max-h-64 overflow-y-auto">
                  {rentals.map(rental => {
                    const selected = selectedRentals.find(r => r.id === rental.id);
                    return (
                      <div
                        key={rental.id}
                        className={`p-3 rounded-lg border transition-all ${
                          selected ? 'border-[#1e3a5f] bg-[#1e3a5f]/10' : 'border-gray-200 bg-white'
                        }`}
                      >
                        <div className="flex justify-between items-center">
                          <div className="cursor-pointer flex-1" onClick={() => toggleRental(rental)}>
                            <div className="flex items-center gap-2">
                              {selected && <Check size={14} className="text-[#1e3a5f]" />}
                              <span className="font-medium text-sm">{rental.name}</span>
                            </div>
                            <span className="text-xs text-gray-500">${rental.price}/each</span>
                          </div>
                          {selected && (
                            <div className="flex items-center gap-1 border rounded">
                              <button
                                onClick={() => updateRentalQty(rental.id, selected.quantity - 1)}
                                className="px-2 py-1 hover:bg-gray-100 text-sm"
                              >-</button>
                              <span className="px-2 text-sm">{selected.quantity}</span>
                              <button
                                onClick={() => updateRentalQty(rental.id, selected.quantity + 1)}
                                className="px-2 py-1 hover:bg-gray-100 text-sm"
                              >+</button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              )}
            </Card>
          </div>

          {/* Right Column - Quote Summary */}
          <div className="lg:col-span-1">
            <div className="sticky top-4">
              <Card className="border-none shadow-xl">
                <CardHeader className="bg-gradient-to-r from-[#1e3a5f] to-[#2d5016] text-white rounded-t-lg">
                  <CardTitle className="flex items-center gap-2">
                    <Calculator size={24} />
                    Your Estimate
                  </CardTitle>
                  <p className="text-sm text-gray-200">{guestCount} guests</p>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  {/* Line Items */}
                  <div className="space-y-3">
                    {totals.menuSubtotal > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Menu Packages</span>
                        <span className="font-medium">${totals.menuSubtotal.toFixed(2)}</span>
                      </div>
                    )}
                    {totals.platedSubtotal > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Plated Dinner</span>
                        <span className="font-medium">${totals.platedSubtotal.toFixed(2)}</span>
                      </div>
                    )}
                    {totals.barSubtotal > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Bar Package</span>
                        <span className="font-medium">${totals.barSubtotal.toFixed(2)}</span>
                      </div>
                    )}
                    {totals.addonsSubtotal > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Add-Ons</span>
                        <span className="font-medium">${totals.addonsSubtotal.toFixed(2)}</span>
                      </div>
                    )}
                    {totals.rentalsSubtotal > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Rentals & Décor</span>
                        <span className="font-medium">${totals.rentalsSubtotal.toFixed(2)}</span>
                      </div>
                    )}
                  </div>

                  <div className="border-t pt-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Subtotal</span>
                      <span className="font-medium">${totals.subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm mt-1">
                      <span className="text-gray-600">HST (13%)</span>
                      <span className="font-medium">${totals.taxAmount.toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Gratuity Toggle */}
                  <div className="border-t pt-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={includeGratuity}
                        onChange={(e) => setIncludeGratuity(e.target.checked)}
                        className="rounded"
                      />
                      <span className="text-sm">Include Gratuity</span>
                    </label>
                    {includeGratuity && (
                      <div className="mt-2 flex gap-2">
                        {[0.15, 0.18, 0.20].map(rate => (
                          <button
                            key={rate}
                            onClick={() => setGratuityRate(rate)}
                            className={`px-3 py-1 rounded text-sm ${
                              gratuityRate === rate
                                ? 'bg-[#1e3a5f] text-white'
                                : 'bg-gray-100 hover:bg-gray-200'
                            }`}
                          >
                            {(rate * 100).toFixed(0)}%
                          </button>
                        ))}
                      </div>
                    )}
                    {includeGratuity && (
                      <div className="flex justify-between text-sm mt-2">
                        <span className="text-gray-600">Gratuity ({(gratuityRate * 100).toFixed(0)}%)</span>
                        <span className="font-medium">${totals.gratuityAmount.toFixed(2)}</span>
                      </div>
                    )}
                  </div>

                  {/* Total */}
                  <div className="border-t-2 border-[#1e3a5f] pt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-bold text-[#1e3a5f]">Estimated Total</span>
                      <span className="text-2xl font-bold text-[#1e3a5f]">${totals.total.toFixed(2)}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      ${(totals.total / guestCount).toFixed(2)} per person
                    </p>
                    {!includeGratuity && (
                      <p className="text-xs text-amber-600 mt-2 font-medium">
                        ⚠️ Gratuity not included in this estimate
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="space-y-3 pt-4">
                    <Button
                      onClick={handleRequestQuote}
                      className="w-full bg-[#c9a227] hover:bg-[#c4a574] text-[#1e3a5f] font-semibold"
                      data-testid="request-quote-btn"
                    >
                      <Send size={18} className="mr-2" />
                      Request Quote
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => window.print()}
                      className="w-full border-[#1e3a5f] text-[#1e3a5f]"
                    >
                      <FileText size={18} className="mr-2" />
                      Print Estimate
                    </Button>
                  </div>

                  <p className="text-xs text-gray-400 text-center">
                    This is an estimate only. Final pricing may vary based on specific requirements.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-[#1e3a5f] text-white py-6 mt-12">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-sm text-gray-300">
            Willow Ridge Catering • Questions? Call us at (555) 123-4567
          </p>
        </div>
      </div>
    </div>
  );
};

export default QuoteCalculator;
